import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router } from '@angular/router';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { Route } from '../enums/route.enum';
import { UserRole } from '../enums/user-roles.enum';
import { UserService } from '../../services/user/user.service';
import { PartySearchContextService } from '@app/services/party-search/party-search-context.service';

/**
 * Redirects CNH internal users (CNHUser / Admin) to the search page when they open
 * a **customer / contract** route (`data.requiresPartySearch === true`) without party context.
 *
 * Portal tooling routes (user management, configuration, etc.) omit that flag and stay reachable.
 * The search page is always allowed (redirect loop guard). {@link UserRole.User} is never blocked here.
 *
 * Waits for {@link UserService#getUserRoleGate$} so the role is resolved before deciding — the sync
 * signal can still be null on the first navigation tick after Auth0 callback.
 */
export const internalPartyContextGuard: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
) => {
  const userService = inject(UserService);
  const partyContext = inject(PartySearchContextService);
  const router = inject(Router);

  if (childRoute.routeConfig?.path === Route.SEARCH_USER) {
    return true;
  }

  const requiresParty = childRoute.data?.['requiresPartySearch'] === true;
  const path = childRoute.routeConfig?.path;

  if (!requiresParty && path) {
    return true;
  }

  if (path === Route.FAQ || path === Route.CONTACT_US || path === Route.ADMIN_CONTACT_US) {
    return true;
  }

  return userService.getUserRoleGate$().pipe(
    take(1),
    map((role) => {
      const isInternal = role === UserRole.CNHUser || role === UserRole.Admin;
      if (!isInternal) {
        return true;
      }

      if (partyContext.params() && partyContext.contractData()) {
        return true;
      }

      return router.parseUrl(`/${Route.SEARCH_USER}`);
    }),
    catchError(() => of(true)),
  );
};
